/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: inherit
 *
 * 4E.3 — kiosk rates platform rider after delivery.
 * Write-once: after submit, thank-you only (no edit → no spam).
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  ActivityIndicator,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { ticketColors, ticketFonts } from '../../../shared/theme/ticketColors';
import { spacing } from '../../../shared/theme/spacing';
import { supabase } from '../../../shared/supabase/supabaseClient';

type Props = {
  orderId: string;
};

type ContextResult = {
  ok?: boolean;
  assignment_id?: string;
  rider_name?: string | null;
  stars?: number | null;
  comment?: string | null;
};

type SubmitResult = {
  ok?: boolean;
  error?: string;
};

export function RiderRatingPanel({ orderId }: Props) {
  const [assignmentId, setAssignmentId] = useState<string | null>(null);
  const [riderName, setRiderName] = useState<string | null>(null);
  const [stars, setStars] = useState(0);
  const [comment, setComment] = useState('');
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const { data, error: rpcErr } = await supabase.rpc('get_order_rider_rating_context', {
        p_order_id: orderId,
        p_rater_role: 'kiosk',
      });
      if (rpcErr) {
        setAssignmentId(null);
        return;
      }
      const ctx = data as ContextResult;
      if (!ctx?.ok || !ctx.assignment_id) {
        setAssignmentId(null);
        return;
      }
      setAssignmentId(ctx.assignment_id);
      setRiderName(ctx.rider_name ?? null);
      if (ctx.stars != null) {
        setStars(Number(ctx.stars));
        setComment(ctx.comment ?? '');
        setSaved(true);
      }
    } catch {
      setAssignmentId(null);
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    void load();
  }, [load]);

  const submit = async () => {
    if (saved) return;
    if (!assignmentId || stars < 1) {
      setError('Elegí de 1 a 5 estrellas.');
      return;
    }
    setSubmitting(true);
    setError(null);
    const { data, error: rpcErr } = await supabase.rpc('submit_rider_rating', {
      p_assignment_id: assignmentId,
      p_stars: stars,
      p_rater_role: 'kiosk',
      p_comment: comment.trim() || null,
    });
    setSubmitting(false);
    if (rpcErr) {
      setError(rpcErr.message);
      return;
    }
    const result = data as SubmitResult;
    if (!result?.ok) {
      if (result?.error === 'already_rated') {
        setSaved(true);
        return;
      }
      setError(result?.error ?? 'No se pudo guardar');
      return;
    }
    setSaved(true);
  };

  if (loading || !assignmentId) return null;

  return (
    <View style={styles.card}>
      <Text style={styles.title}>
        {saved ? 'Calificación al repartidor' : 'Calificar repartidor'}
      </Text>
      <Text style={styles.hint}>
        {riderName ? `¿Cómo fue ${riderName}?` : '¿Cómo fue la entrega?'}
      </Text>

      <View style={styles.starsRow}>
        {[1, 2, 3, 4, 5].map((n) =>
          saved ? (
            <View key={n} accessibilityElementsHidden>
              <Ionicons
                name={stars >= n ? 'star' : 'star-outline'}
                size={28}
                color={stars >= n ? ticketColors.gold : ticketColors.ink3}
              />
            </View>
          ) : (
            <Pressable
              key={n}
              onPress={() => setStars(n)}
              disabled={submitting}
              accessibilityRole="button"
              accessibilityLabel={`${n} estrellas`}
              hitSlop={6}
            >
              <Ionicons
                name={stars >= n ? 'star' : 'star-outline'}
                size={28}
                color={stars >= n ? ticketColors.gold : ticketColors.ink3}
              />
            </Pressable>
          ),
        )}
      </View>

      {saved ? (
        <>
          {comment ? <Text style={styles.commentSaved}>“{comment}”</Text> : null}
          <Text style={styles.ok}>Calificación guardada</Text>
        </>
      ) : (
        <>
          <TextInput
            value={comment}
            onChangeText={setComment}
            placeholder="Un comentario, si querés"
            placeholderTextColor={ticketColors.ink2}
            editable={!submitting}
            multiline
            maxLength={500}
            style={styles.input}
          />

          {error ? <Text style={styles.error}>{error}</Text> : null}

          <Pressable
            style={[styles.btn, (submitting || stars < 1) && styles.btnDisabled]}
            onPress={() => void submit()}
            disabled={submitting || stars < 1}
          >
            {submitting ? (
              <ActivityIndicator color={ticketColors.goldInk} />
            ) : (
              <Text style={styles.btnText}>Mandar calificación</Text>
            )}
          </Pressable>
        </>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    gap: 6,
    padding: spacing.md,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
    marginTop: spacing.sm,
  },
  title: {
    fontSize: 14,
    fontWeight: '800',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  hint: {
    fontSize: 13,
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
  },
  starsRow: {
    flexDirection: 'row',
    gap: 6,
    marginTop: 4,
  },
  input: {
    marginTop: 4,
    minHeight: 56,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    borderRadius: 10,
    paddingHorizontal: 10,
    paddingVertical: 8,
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
    fontSize: 14,
    textAlignVertical: 'top',
  },
  commentSaved: {
    marginTop: 4,
    fontSize: 13,
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
  },
  error: {
    fontSize: 13,
    color: ticketColors.bad,
    fontFamily: ticketFonts.sans,
  },
  ok: {
    marginTop: 4,
    fontSize: 13,
    fontWeight: '700',
    color: ticketColors.ok,
    fontFamily: ticketFonts.sans,
  },
  btn: {
    marginTop: 6,
    backgroundColor: ticketColors.gold,
    borderRadius: 10,
    paddingVertical: 12,
    alignItems: 'center',
  },
  btnDisabled: { opacity: 0.5 },
  btnText: {
    fontSize: 14,
    fontWeight: '800',
    color: ticketColors.goldInk,
    fontFamily: ticketFonts.sans,
  },
});
